/**
 * Low-level Unicode helpers for Modern Greek text comparison.
 *
 * Design rules (spec §5.1):
 *  - Everything is NFC-normalised before comparison; offsets always refer to the NFC string.
 *  - Diacritics are NEVER stripped for scoring; `fold()` is only used to decide *which* words align.
 *  - The Greek tonos (U+0301 after NFD, or precomposed ά έ ή ί ό ύ ώ ΐ ΰ) is an accent;
 *    an apostrophe (’ ' ʼ ‘) is an elision mark and is never treated as an accent.
 */

export const COMBINING_ACUTE = '́'; // tonos / oxia after NFD
export const COMBINING_DIAERESIS = '̈'; // dialytika
/** Standalone tonos sign (U+0384) and dialytika-tonos (U+0385) as typed by some keyboards/OCR. */
export const SPACING_TONOS = new Set(['΄', '΅', '´']);

/** Elision apostrophes considered equivalent to each other. */
export const APOSTROPHES = new Set(["'", '’', 'ʼ', '‘', '᾽', '᾿']);
export const CANONICAL_APOSTROPHE = '’';

export function nfc(s: string): string {
  return s.normalize('NFC');
}

const segmenter: Intl.Segmenter | null =
  typeof Intl !== 'undefined' && 'Segmenter' in Intl ? new Intl.Segmenter('el', { granularity: 'grapheme' }) : null;

export interface Grapheme {
  /** Raw grapheme cluster (NFC). */
  text: string;
  /** Offset (UTF-16 code units) in the source string. */
  start: number;
  end: number;
}

/** Split into extended grapheme clusters with offsets. Falls back to code points + combining marks. */
export function graphemes(s: string): Grapheme[] {
  const out: Grapheme[] = [];
  if (segmenter) {
    for (const seg of segmenter.segment(s)) {
      out.push({ text: seg.segment, start: seg.index, end: seg.index + seg.segment.length });
    }
    return out;
  }
  let i = 0;
  while (i < s.length) {
    const cp = s.codePointAt(i)!;
    let j = i + (cp > 0xffff ? 2 : 1);
    while (j < s.length && /\p{M}/u.test(s[j]!)) j++;
    out.push({ text: s.slice(i, j), start: i, end: j });
    i = j;
  }
  return out;
}

export interface CharInfo {
  /** Lowercased base letter with marks removed; final sigma folded to σ. */
  base: string;
  /** Base letter exactly as written (case preserved, sigma form preserved). */
  rawBase: string;
  hasTonos: boolean;
  hasDiaeresis: boolean;
  /** Other combining marks (rare in monotonic Greek: e.g. polytonic breathings). */
  otherMarks: string;
  isUpper: boolean;
  isFinalSigma: boolean;
  isSigma: boolean;
}

export function charInfo(g: string): CharInfo {
  const d = g.normalize('NFD');
  let rawBase = '';
  let hasTonos = false;
  let hasDiaeresis = false;
  let otherMarks = '';
  for (const ch of d) {
    if (ch === COMBINING_ACUTE) hasTonos = true;
    else if (ch === COMBINING_DIAERESIS) hasDiaeresis = true;
    else if (/\p{M}/u.test(ch)) otherMarks += ch;
    else rawBase += ch;
  }
  if (SPACING_TONOS.has(rawBase)) {
    // A standalone tonos sign: treat as an accent with no base letter.
    hasTonos = true;
  }
  const lower = rawBase.toLocaleLowerCase('el');
  const isFinalSigma = rawBase === 'ς';
  const isSigma = lower === 'σ' || lower === 'ς';
  return {
    base: isSigma ? 'σ' : lower,
    rawBase,
    hasTonos,
    hasDiaeresis,
    otherMarks,
    isUpper: rawBase !== lower && rawBase.toLocaleUpperCase('el') === rawBase,
    isFinalSigma,
    isSigma,
  };
}

/** Accent-, case- and sigma-insensitive form used ONLY for alignment decisions. */
export function fold(s: string): string {
  let out = '';
  for (const ch of s.normalize('NFD')) {
    if (/\p{M}/u.test(ch)) continue;
    if (SPACING_TONOS.has(ch)) continue;
    if (APOSTROPHES.has(ch)) {
      out += CANONICAL_APOSTROPHE;
      continue;
    }
    const l = ch.toLocaleLowerCase('el');
    out += l === 'ς' ? 'σ' : l;
  }
  return out;
}

/** Exact comparison key: NFC, apostrophe variants unified. Accents, case and sigma form preserved. */
export function exactKey(s: string): string {
  let out = '';
  for (const ch of nfc(s)) out += APOSTROPHES.has(ch) ? CANONICAL_APOSTROPHE : ch;
  return out;
}

export function isGreekLetter(ch: string): boolean {
  return /[Ͱ-Ͽἀ-῿]/u.test(ch) && /\p{L}/u.test(ch);
}

export function isLetterLike(g: string): boolean {
  return /[\p{L}\p{N}]/u.test(g);
}

/** Count tonos marks in a word. */
export function tonosPositions(word: string): number[] {
  const res: number[] = [];
  graphemes(nfc(word)).forEach((g, i) => {
    if (charInfo(g.text).hasTonos) res.push(i);
  });
  return res;
}

/** Count words the way exam word limits are usually counted: whitespace tokens containing a letter/digit. */
export function countWords(text: string): number {
  return nfc(text)
    .split(/\s+/u)
    .filter((t) => /[\p{L}\p{N}]/u.test(t)).length;
}

/** Latin letters that look like Greek ones — a frequent source of invisible copy errors in content. */
export const LATIN_LOOKALIKES = /[AaBEeHIiKkMNOoPpTtXxYyZzvu]/;

export function findMixedScriptWords(text: string): string[] {
  const words = nfc(text).split(/[^\p{L}\p{M}]+/u).filter(Boolean);
  return words.filter((w) => /\p{Script=Greek}/u.test(w) && /\p{Script=Latin}/u.test(w));
}
